<template>
  <AppDialog
    v-model="dialog"
    :title="title"
    max-width="1500px"
    scrollable
    @close="handleClose"
  >
    <v-card-text>
      <v-form
        ref="form"
        v-model="valid"
      >
        <v-row class="pt-4">
          <v-col cols="12">
            <v-row>
              <v-col cols="4">
                <v-select
                  v-model="sighting.sightingType"
                  name="sighting_type"
                  label="Sighting Type*"
                  required
                  density="compact"
                  :readonly="!wantEdit"
                  :rules="rules"
                  :items="Object.values(enums.sightingTypes)"
                />
              </v-col>
              <v-col cols="4">
                <v-text-field
                  v-if="sighting.id"
                  v-model="sighting.sightingNumber"
                  name="sighting_number"
                  label="Sighting Number*"
                  required
                  disabled
                  density="compact"
                  :readonly="!wantEdit"
                  :rules="rules"
                />
              </v-col>
              <v-col cols="4">
                <v-text-field
                  v-if="sighting.id"
                  v-model="sighting.sightingUuid"
                  name="sighting_uuid"
                  label="Sighting Uuid*"
                  disabled
                  density="compact"
                />
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="6">
            <v-row>
              <v-col cols="12">
                <div
                  class="font-weight-bold pt-4 text-h6"
                >
                  Sighting Info
                </div>
                <v-divider class="divider" />
                <v-row>
                  <v-col cols="3">
                    <v-text-field
                      v-model="sighting.sightingYear"
                      name="sighting_year"
                      label="Year*"
                      required
                      type="number"
                      density="compact"
                      hide-spin-buttons
                      :readonly="!wantEdit"
                      :rules="rules"
                    />
                  </v-col>
                  <v-col cols="2">
                    <v-text-field
                      v-model="sighting.sightingMonth"
                      name="sighting_month"
                      label="Month"
                      type="number"
                      density="compact"
                      hide-spin-buttons
                      :min="1"
                      :max="12"
                      :readonly="!wantEdit"
                    />
                  </v-col>
                  <v-col cols="2">
                    <v-text-field
                      v-model="sighting.sightingDay"
                      name="sighting_day"
                      label="Day*"
                      required
                      type="number"
                      density="compact"
                      hide-spin-buttons
                      :min="1"
                      :max="maxDay"
                      :readonly="!wantEdit"
                      :rules="rules"
                    />
                  </v-col>
                  <v-col cols="3">
                    <v-text-field
                      v-model="sighting.sightingTime"
                      name="sighting_time"
                      label="Time"
                      density="compact"
                      :readonly="!wantEdit"
                      @keypress="filterTimeInput"
                    />
                  </v-col>
                </v-row>
              </v-col>
            </v-row>

            <!-- Location Section -->
            <div
              class="font-weight-bold pt-4 text-h6"
            >
              Location
            </div>
            <v-divider class="divider" />
            <v-row>
              <v-col cols="3">
                <v-select
                  v-model="coordSystem"
                  :items="['DD', 'DDM', 'DMS']"
                  label="Coord System"
                  density="compact"
                  :readonly="!wantEdit"
                  hide-details
                />
              </v-col>
              <v-col cols="5">
                <v-select
                  v-model="selectedKnownLocation"
                  :items="knownLocationItems"
                  item-title="name"
                  item-value="key"
                  label="Known Location"
                  density="compact"
                  hide-details
                  clearable
                  :readonly="!wantEdit"
                  @update:model-value="applyKnownLocation"
                />
              </v-col>
              <v-col cols="4">
                <v-checkbox
                  id="isNominalCoordinates"
                  v-model="sighting.isNominalCoordinates"
                  label="Nominal Coordinates?"
                  density="compact"
                  hide-details
                  :readonly="!wantEdit"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="10">
                <!-- DD -->
                <template v-if="coordSystem === 'DD'">
                  <v-row>
                    <v-col cols="6">
                      <v-text-field
                        v-model="sighting.lat"
                        name="lat"
                        label="Latitude*"
                        :rules="lonLatRules"
                        type="number"
                        density="compact"
                        :readonly="!wantEdit"
                        hide-spin-buttons
                        @change="sighting.coordinateDisplaySystem = coordSystem"
                      >
                        <template #append-inner>
                          &deg;
                        </template>
                      </v-text-field>
                    </v-col>
                    <v-col cols="6">
                      <v-text-field
                        v-model="sighting.lon"
                        name="lon"
                        label="Longitude*"
                        :rules="lonLatRules"
                        type="number"
                        density="compact"
                        :readonly="!wantEdit"
                        hide-spin-buttons
                        @change="sighting.coordinateDisplaySystem = coordSystem"
                      >
                        <template #append-inner>
                          &deg;
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>
                </template>

                <!-- DDM -->
                <template v-else-if="coordSystem === 'DDM'">
                  <v-row
                    align="center"
                    class="mb-1"
                  >
                    <v-col
                      cols="3"
                      class="pa-1"
                    >
                      <v-btn-toggle
                        v-model="coordInputs.latDir"
                        density="compact"
                        :disabled="!wantEdit"
                        @update:model-value="syncInputsToDD"
                      >
                        <v-btn
                          value="N"
                          size="small"
                        >
                          N
                        </v-btn>
                        <v-btn
                          value="S"
                          size="small"
                        >
                          S
                        </v-btn>
                      </v-btn-toggle>
                    </v-col>
                    <v-col
                      cols="4"
                      class="pa-1"
                    >
                      <v-text-field
                        v-model="coordInputs.latDeg"
                        label="Deg"
                        type="number"
                        density="compact"
                        hide-details
                        hide-spin-buttons
                        :readonly="!wantEdit"
                        @change="syncInputsToDD"
                      >
                        <template #append-inner>
                          &deg;
                        </template>
                      </v-text-field>
                    </v-col>
                    <v-col
                      cols="5"
                      class="pa-1"
                    >
                      <v-text-field
                        v-model="coordInputs.latMin"
                        label="Min"
                        type="number"
                        density="compact"
                        hide-details
                        hide-spin-buttons
                        :readonly="!wantEdit"
                        @change="syncInputsToDD"
                      >
                        <template #append-inner>
                          '
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>
                  <v-row align="center">
                    <v-col
                      cols="3"
                      class="pa-1"
                    >
                      <v-btn-toggle
                        v-model="coordInputs.lonDir"
                        density="compact"
                        :disabled="!wantEdit"
                        @update:model-value="syncInputsToDD"
                      >
                        <v-btn
                          value="E"
                          size="small"
                        >
                          E
                        </v-btn>
                        <v-btn
                          value="W"
                          size="small"
                        >
                          W
                        </v-btn>
                      </v-btn-toggle>
                    </v-col>
                    <v-col
                      cols="4"
                      class="pa-1"
                    >
                      <v-text-field
                        v-model="coordInputs.lonDeg"
                        label="Deg"
                        type="number"
                        density="compact"
                        hide-details
                        hide-spin-buttons
                        :readonly="!wantEdit"
                        @change="syncInputsToDD"
                      >
                        <template #append-inner>
                          &deg;
                        </template>
                      </v-text-field>
                    </v-col>
                    <v-col
                      cols="5"
                      class="pa-1"
                    >
                      <v-text-field
                        v-model="coordInputs.lonMin"
                        label="Min"
                        type="number"
                        density="compact"
                        hide-details
                        hide-spin-buttons
                        :readonly="!wantEdit"
                        @change="syncInputsToDD"
                      >
                        <template #append-inner>
                          '
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>
                </template>

                <!-- DMS -->
                <template v-else-if="coordSystem === 'DMS'">
                  <v-row
                    align="center"
                    class="mb-1"
                  >
                    <v-col
                      cols="2"
                      class="pa-1"
                    >
                      <v-btn-toggle
                        v-model="coordInputs.latDir"
                        density="compact"
                        :disabled="!wantEdit"
                        @update:model-value="syncInputsToDD"
                      >
                        <v-btn
                          value="N"
                          size="small"
                        >
                          N
                        </v-btn>
                        <v-btn
                          value="S"
                          size="small"
                        >
                          S
                        </v-btn>
                      </v-btn-toggle>
                    </v-col>
                    <v-col
                      cols="3"
                      class="pa-1"
                    >
                      <v-text-field
                        v-model="coordInputs.latDeg"
                        label="Deg"
                        type="number"
                        density="compact"
                        hide-details
                        hide-spin-buttons
                        :readonly="!wantEdit"
                        @change="syncInputsToDD"
                      >
                        <template #append-inner>
                          &deg;
                        </template>
                      </v-text-field>
                    </v-col>
                    <v-col
                      cols="3"
                      class="pa-1"
                    >
                      <v-text-field
                        v-model="coordInputs.latMin"
                        label="Min"
                        type="number"
                        density="compact"
                        hide-details
                        hide-spin-buttons
                        :readonly="!wantEdit"
                        @change="syncInputsToDD"
                      >
                        <template #append-inner>
                          '
                        </template>
                      </v-text-field>
                    </v-col>
                    <v-col
                      cols="4"
                      class="pa-1"
                    >
                      <v-text-field
                        v-model="coordInputs.latSec"
                        label="Sec"
                        type="number"
                        density="compact"
                        hide-details
                        hide-spin-buttons
                        :readonly="!wantEdit"
                        @change="syncInputsToDD"
                      >
                        <template #append-inner>
                          "
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>
                  <v-row align="center">
                    <v-col
                      cols="2"
                      class="pa-1"
                    >
                      <v-btn-toggle
                        v-model="coordInputs.lonDir"
                        density="compact"
                        :disabled="!wantEdit"
                        @update:model-value="syncInputsToDD"
                      >
                        <v-btn
                          value="E"
                          size="small"
                        >
                          E
                        </v-btn>
                        <v-btn
                          value="W"
                          size="small"
                        >
                          W
                        </v-btn>
                      </v-btn-toggle>
                    </v-col>
                    <v-col
                      cols="3"
                      class="pa-1"
                    >
                      <v-text-field
                        v-model="coordInputs.lonDeg"
                        label="Deg"
                        type="number"
                        density="compact"
                        hide-details
                        hide-spin-buttons
                        :readonly="!wantEdit"
                        @change="syncInputsToDD"
                      >
                        <template #append-inner>
                          &deg;
                        </template>
                      </v-text-field>
                    </v-col>
                    <v-col
                      cols="3"
                      class="pa-1"
                    >
                      <v-text-field
                        v-model="coordInputs.lonMin"
                        label="Min"
                        type="number"
                        density="compact"
                        hide-details
                        hide-spin-buttons
                        :readonly="!wantEdit"
                        @change="syncInputsToDD"
                      >
                        <template #append-inner>
                          '
                        </template>
                      </v-text-field>
                    </v-col>
                    <v-col
                      cols="4"
                      class="pa-1"
                    >
                      <v-text-field
                        v-model="coordInputs.lonSec"
                        label="Sec"
                        type="number"
                        density="compact"
                        hide-details
                        hide-spin-buttons
                        :readonly="!wantEdit"
                        @change="syncInputsToDD"
                      >
                        <template #append-inner>
                          "
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>
                </template>
              </v-col>
              <v-col
                cols="2"
                class="d-flex align-center justify-center"
              >
                <v-menu
                  v-model="mapField"
                  :close-on-content-click="false"
                  :offset="40"
                  transition="scale-transition"
                  min-width="auto"
                  class="map"
                  :disabled="!wantEdit"
                >
                  <template #activator="{ props }">
                    <v-tooltip location="bottom">
                      <template #activator="{ props: tooltipProps }">
                        <v-btn
                          size="small"
                          v-bind="{ ...props, ...tooltipProps }"
                        >
                          <v-icon>mdi-map-marker-account</v-icon>
                        </v-btn>
                      </template>
                      <span>Open Map</span>
                    </v-tooltip>
                  </template>
                  <v-row class="map-container">
                    <div
                      id="map"
                      class="map"
                    />
                  </v-row>
                  <v-row class="map-buttons">
                    <v-text-field
                      v-model="mapLocation[1]"
                      name="lat"
                      label="Sighting Location Latitude"
                      :rules="lonLatRules"
                      type="number"
                      :readonly="!wantEdit"
                      class="mx-2"
                      density="compact"
                    />
                    <v-text-field
                      v-model="mapLocation[0]"
                      name="lon"
                      label="Sighting Location Longitude"
                      :rules="lonLatRules"
                      type="number"
                      :readonly="!wantEdit"
                      class="mx-2"
                      density="compact"
                    />
                    <v-spacer />
                    <v-btn
                      color="primary"
                      class="py-4 mb-2"
                      size="small"
                      @click="saveLocation"
                    >
                      <v-icon>mdi-check</v-icon>
                      SAVE
                    </v-btn>
                  </v-row>
                </v-menu>
              </v-col>
            </v-row>

            <div
              class="font-weight-bold pt-4 text-h6"
            >
              Environment
            </div>
            <v-divider class="divider" />
            <v-row>
              <v-col cols="5">
                <v-select
                  v-model="sighting.weather"
                  name="weather"
                  label="Weather*"
                  density="compact"
                  :items="Object.values(enums.sightingWeather)"
                  :rules="rules"
                  :readonly="!wantEdit"
                />
              </v-col>
              <v-col cols="3">
                <v-text-field
                  v-model="sighting.cloudCover"
                  name="cloud_cover"
                  label="Cloud %*"
                  required
                  type="number"
                  density="compact"
                  hide-spin-buttons
                  :rules="rules"
                  :readonly="!wantEdit || sameVessel"
                />
              </v-col>
              <v-col cols="4">
                <v-select
                  v-model="sighting.beaufort"
                  name="beaufort"
                  label="Beaufort"
                  density="compact"
                  :items="beaufortItems"
                  required
                  :rules="rules"
                  :readonly="!wantEdit || sameVessel"
                />
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="6">
            <div
              class="font-weight-bold pt-4 text-h6"
            >
              Sighting Details
            </div>
            <v-divider class="divider" />
            <v-row>
              <v-col cols="3">
                <v-select
                  v-model="sighting.species"
                  :items="Object.values(enums.sightingSpecies)"
                  label="Species*"
                  density="compact"
                  :rules="rules"
                  :readonly="!wantEdit"
                />
              </v-col>
              <v-col
                v-if="sighting.species === 'Other'"
                cols="3"
              >
                <v-text-field
                  v-model="specOtherValue"
                  name="other_species"
                  label="Specify*"
                  required
                  density="compact"
                  :rules="rules"
                  :readonly="!wantEdit"
                />
              </v-col>
              <v-col :cols="sighting.species === 'Other' ? 4 : 5">
                <v-text-field
                  v-model="sighting.locationDescription"
                  name="location"
                  label="Location"
                  required
                  density="compact"
                  :rules="rules"
                  :readonly="!wantEdit || sameVessel"
                />
              </v-col>
              <v-col cols="2">
                <v-text-field
                  v-model="sighting.dataEnteredBy"
                  name="data_entered_by"
                  label="By"
                  required
                  density="compact"
                  :rules="rules"
                  :readonly="!wantEdit || sameVessel"
                />
              </v-col>
              <v-col :cols="sighting.species === 'Other' ? 12 : 2">
                <v-text-field
                  v-model="sighting.effort"
                  name="effort"
                  label="Effort"
                  required
                  type="number"
                  density="compact"
                  hide-spin-buttons
                  :rules="rules"
                  :readonly="!wantEdit || sameVessel"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="3">
                <v-select
                  v-model="sighting.droneUp"
                  name="drone_up"
                  label="Drone Up"
                  density="compact"
                  :items="[{ title: 'Yes', value: '1' }, { title: 'No', value: '0' }, { title: 'NA', value: 'NA' }]"
                  item-title="title"
                  item-value="value"
                  :readonly="!wantEdit || sameVessel"
                />
              </v-col>
              <v-col cols="3">
                <v-select
                  v-model="sighting.withinHydrophoneArray"
                  name="within_array"
                  label="In Array"
                  density="compact"
                  :items="['0', '1', '2', '3', 'NA']"
                  :readonly="!wantEdit || sameVessel"
                />
              </v-col>
              <v-col cols="3">
                <v-text-field
                  v-model="sighting.totalGroupSize"
                  name="total_group_size"
                  label="Group Size"
                  type="number"
                  density="compact"
                  hide-spin-buttons
                  :readonly="!wantEdit"
                />
              </v-col>
              <v-col cols="3">
                <v-text-field
                  v-model="sighting.totalBnfAll"
                  name="total_bnf"
                  label="Total BNF"
                  type="number"
                  density="compact"
                  hide-spin-buttons
                  :readonly="!wantEdit"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-textarea
                  v-model="sighting.comments"
                  name="comments"
                  label="Comments"
                  variant="outlined"
                  density="compact"
                  rows="2"
                  auto-grow
                  :readonly="!wantEdit"
                />
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="12">
            <div
              class="font-weight-bold pt-4 text-h6"
            >
              Whales
            </div>
            <v-divider class="divider" />
            <v-row>
              <v-col cols="12">
                <v-btn
                  size="small"
                  color="primary"
                  :disabled="!wantEdit"
                  @click="addWhale"
                >
                  <v-icon>mdi-plus</v-icon>
                  Add Whale
                </v-btn>
                <v-btn
                  size="small"
                  class="ml-2"
                  :disabled="!wantEdit"
                  @click="syncGroupSizeAndBnf"
                >
                  <v-icon>mdi-sync</v-icon>
                  Sync Group Size & BNF to Table
                </v-btn>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-table
                  v-if="whales"
                  density="compact"
                >
                  <thead>
                    <tr>
                      <th class="text-left">
                        Id
                      </th>
                      <th class="text-left">
                        Group Number
                      </th>
                      <th class="text-left">
                        Name
                      </th>
                      <th class="text-left">
                        Features
                      </th>
                      <th class="text-left">
                        Behaviours
                      </th>
                      <th class="text-left">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="item of whales"
                      :key="item.whaleId"
                    >
                      <td>{{ item.whaleId }}</td>
                      <td>{{ item.groupNumber }}{{ item.groupMemberId }}</td>
                      <td>{{ item.whaleName }}</td>
                      <td>{{ (item.features || []).map(f => f.featureName).filter(Boolean).join(', ') }}</td>
                      <td>{{ item.bhv }}</td>
                      <td>
                        <v-tooltip loaction="bottom">
                          <template #activator="{ props }">
                            <v-icon
                              size="small"
                              class="mr-2"
                              v-bind="props"
                              :disabled="editWhaleDialog"
                              v-on="props"
                              @click="editWhale(item)"
                            >
                              mdi-pencil
                            </v-icon>
                          </template>
                          <span>Edit Whale</span>
                        </v-tooltip>
                        <v-tooltip location="bottom">
                          <template #activator="{ props }">
                            <v-icon
                              size="small"
                              v-bind="props"
                              :disabled="deleteWhaleDialog"
                              v-on="props"
                              @click="deleteWhale(item)"
                            >
                              mdi-delete
                            </v-icon>
                          </template>
                          <span>Delete Whale</span>
                        </v-tooltip>
                      </td>
                    </tr>
                  </tbody>
                </v-table>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>
    <v-card-actions>
      <v-btn
        v-if="!viewOnly"
        @click="handleClose"
      >
        <v-icon>mdi-close</v-icon>
        Cancel
      </v-btn>
      <v-spacer />
      <v-btn
        v-if="sighting.id && !viewOnly && hasScope('sightings:write')"
        :disabled="wantEdit"
        @click="toggleEditConfirmDialog(true)"
      >
        <v-icon>mdi-pencil</v-icon>
        Enable Editing
      </v-btn>
      <v-btn
        v-if="viewOnly && hasScope('sightings:write')"
        @click="jumpToEdit"
      >
        <v-icon>mdi-pencil</v-icon>
        Edit this sighting
      </v-btn>
      <v-spacer />
      <v-btn
        v-if="sighting.id && hasScope('sightings:write')"
        @click="toggleEmailDialog(true)"
      >
        <v-icon class="pr-1">
          mdi-email
        </v-icon>
        Send Email
      </v-btn>
      <v-spacer />
      <v-btn
        v-if="!viewOnly"
        color="primary"
        :disabled="!valid || !wantEdit || saving"
        @click="save"
      >
        <v-icon>mdi-check</v-icon>
        <span v-show="!saving">{{ button }}</span>
        <pulse-loader :loading="saving" />
      </v-btn>
    </v-card-actions>
  </AppDialog>
  <edit-confirm
    :action="enableEditing"
    :dialog="editConfirmDialog"
    :close="toggleEditConfirmDialog"
  />
  <email
    :dialog="emailDialog"
    :close="toggleEmailDialog"
  />
  <NewWhale />
  <delete
    :id="selectedWhale.id"
    :name="selectedWhale.name"
    :delete-action="() => removeWhaleFromList(selectedWhale.id)"
  />
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import AppDialog from "@/components/AppDialog";
import Utils from "@/helpers/utils";
import PulseLoader from "vue-spinner/src/PulseLoader";
import enums from "@/helpers/enums";
import mapboxgl from "mapbox-gl"
import editConfirm from "@/components/EditConfirm.vue";
import email from "@/components/Email.vue";
import "mapbox-gl/dist/mapbox-gl.css";
import NewWhale from "./NewWhale.vue";
import Delete from "../Delete.vue";

const ORCA_CODES = ["NRKW", "BIGGS", "Oo"];

export default {
  components: {
    AppDialog,
    PulseLoader,
    editConfirm,
    email,
    NewWhale,
    Delete,
  },

  props: {
    viewOnly: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      accessToken: process.env.VUE_APP_MAPBOX_TOKEN,
      mapStyle: "mapbox://styles/mapbox/outdoors-v11",
      mapCenter: [-128.9110, 54.6057],
      zoom: 4.15,
      map: {},
      marker: null,
      navigation: null,
      geolocation: null,
      scale: null,
      selectedSighting: {},
      lastVisitDateField: false,
      birthdateDateField: false,
      sightingTimeDateField: false,
      tempDateSighting: null,
      hour: null,
      minute: null,
      mapField: false,
      mapLocation: [],
      wantEdit: false,
      valid: true,
      rules: [(v) => !!v || "This field is required!"],
      lonLatRules: [(v) => !!v || "Please enter a number!"],
      hourRules: [
        (v) =>
          (v && v >= 0) ||
          "Hour should be between 0 (midnight) and 23 (11:00 PM).",
        (v) =>
          (v && v <= 23) ||
          "Hour should be between 0 (midnight) and 23 (11:00 PM).",
      ],
      minuteRules: [
        (v) => (v && v >= 0) || "Minute should be between 0 and 59.",
        (v) => (v && v <= 59) || "Minute should be between 0 and 59.",
      ],
      specOtherValue: "",
      incOtherValue: "",
      locOtherValue: "",
      editConfirmDialog: false,
      emailDialog: false,
      sameVessel: false,
      selectedWhale: {},
      // Coordinate system
      coordSystem: 'DD',
      coordInputs: {
        latDeg: '', latMin: '', latSec: '', latDir: 'N',
        lonDeg: '', lonMin: '', lonSec: '', lonDir: 'W',
      },
      selectedKnownLocation: null,
    };
  },

  computed: {
    enums() {
      return enums;
    },
    ...mapGetters({
      saving: "sighting/details/saving",
      saved: "sighting/details/saved",
      sighting: "sighting/details/sighting",
      dialog: "sighting/details/dialog",
      button: "sighting/details/button",
      title: "sighting/details/title",
      specOtherValueGetter: "sighting/details/specOtherValue",
      incOtherValueGetter: "sighting/details/incOtherValue",
      locOtherValueGetter: "sighting/details/locOtherValue",
      editWhaleDialog: "sighting/whale/dialog",
      deleteWhaleDialog: "delete/dialog",
      whales: "sighting/details/whales",
      hasScope: "auth/hasScope"
    }),
    sightingDatetime() {
      if (this.tempDateSighting) {
        return this.tempDateSighting?.toString();
      } else {
        return "";
      }
    },
    sightingYear() {
      return this.sightingDatetime?.split(" ")[3];
    },
    sightingMonth() {
      const numericalMonth = this.enums.shortMonths.indexOf(this.sightingDatetime?.split(" ")[1]) + 1;
      if (numericalMonth < 10) {
        return `0${numericalMonth}`;
      } else {
        return numericalMonth;
      }
    },
    sightingDay() {
      return this.sightingDatetime?.split(" ")[2];
    },
    sightingDate() {
      return `${this.sightingYear}-${this.sightingMonth}-${this.sightingDay}`;
    },
    sightingTime() {
      return this.sightingDatetime?.split(" ")[4];
    },
    formattedDate: {
      get() {
        return Utils.formatIsoDate(this.sighting.sightingTime);
      },
      set(newVal) {
        this.sighting.sightingTime = Utils.toIsoDateString(newVal);
      },
    },
    validDateSighting() {
      return this.tempDateSighting && this.hour && this.minute;
    },
    beaufortItems() {
      const items = [];
      for (let i = 0; i <= 24; i++) {
        items.push(String(i * 0.5));
      }
      return items;
    },
    maxDay() {
      const month = parseInt(this.sighting.sightingMonth);
      const year = parseInt(this.sighting.sightingYear);
      if (!month) return 31;
      if ([4, 6, 9, 11].includes(month)) return 30;
      if (month === 2) {
        if (year && ((year % 4 === 0 && year % 100 !== 0) || year % 400 === 0)) return 29;
        return 28;
      }
      return 31;
    },
    knownLocationItems() {
      return Object.entries(enums.knownLandSightingLocations)
        .filter(([, v]) => v.in_list)
        .map(([k, v]) => ({
          key: k,
          name: k.replace(/_/g, ' ').replace(/\w\S*/g, w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()),
          lat: v.lat,
          lon: v.lon,
        }));
    },
  },

  watch: {
    coordSystem(newSystem) {
      this.syncDDToInputs(newSystem);
    },
    sighting() {
      this.selectedSighting = this.sighting;
      this.wantEdit = this.viewOnly ? false : !this.sighting.id;
      this.coordSystem = this.sighting.id ? (this.sighting.coordinateDisplaySystem || 'DD') : 'DD';
      this.selectedKnownLocation = null;
      this.syncDDToInputs(this.coordSystem);
      this.datetime = this.sighting?.sightingTime?.split(" ");
      if (Array.isArray(this.datetime)) {
        this.time = this.datetime[1];
        this.hour = this.time?.split(":")[0];
        this.minute = this.time?.split(":")[1];
        this.tempDateSighting = this.datetime[0];
      }
    },
    hour() {
      if (this.hour < 0) {
        this.hour = 23;
      } else if (this.hour > 23) {
        this.hour = 0;
      }
    },
    minute() {
      if (this.minute < 0) {
        this.minute = 59;
      } else if (this.minute > 59) {
        this.minute = 0;
      }
    },
    specOtherValueGetter() {
      this.specOtherValue = this.specOtherValueGetter;
    },
    incOtherValueGetter() {
      this.incOtherValue = this.incOtherValueGetter;
    },
    locOtherValueGetter() {
      this.locOtherValue = this.locOtherValueGetter;
    },
    async mapField() {
      await this.$nextTick();
      setTimeout(() => this.createMap(), 100);
    },
    sameVessel() {
      if (this.sameVessel) {
        this.sighting.incidentVesselName = this.sighting.vesselName;
        this.sighting.incidentVesselType = this.sighting.vesselType;
      } else {
        this.sighting.incidentVesselName = "";
        this.sighting.incidentVesselType = "";
      }
    },
    "sighting.vesselName"() {
      if (this.sameVessel) {
        this.sighting.incidentVesselName = this.sighting.vesselName;
      }
    },
    "sighting.vesselType"() {
      if (this.sameVessel) {
        this.sighting.incidentVesselType = this.sighting.vesselType;
      }
    },
  },
  created() {
    console.log(this.whales)
  },

  methods: {
    ...mapActions({
      update: "sighting/details/update",
      create: "sighting/details/create",
      close: "sighting/details/close",
      openWhaleDialog: "sighting/whale/open",
      openDelete: "delete/open",
      removeFromSightingWhales: "sighting/details/removeFromSightingWhales",
      requestEdit: "sighting/details/requestEdit"
    }),

    ddToDDM(dd, isLat) {
      const val = parseFloat(dd);
      if (isNaN(val)) return { deg: '', min: '', dir: isLat ? 'N' : 'W' };
      const dir = isLat ? (val >= 0 ? 'N' : 'S') : (val >= 0 ? 'E' : 'W');
      const abs = Math.abs(val);
      const deg = Math.floor(abs);
      const min = ((abs - deg) * 60).toFixed(4);
      return { deg: String(deg), min: String(min), dir };
    },

    ddToDMS(dd, isLat) {
      const val = parseFloat(dd);
      if (isNaN(val)) return { deg: '', min: '', sec: '', dir: isLat ? 'N' : 'S' };
      const dir = isLat ? (val >= 0 ? 'N' : 'S') : (val >= 0 ? 'E' : 'W');
      const abs = Math.abs(val);
      const deg = Math.floor(abs);
      const minFull = (abs - deg) * 60;
      const min = Math.floor(minFull);
      const sec = ((minFull - min) * 60).toFixed(2);
      return { deg: String(deg), min: String(min), sec: String(sec), dir };
    },

    ddmToDD(deg, min, dir) {
      const dd = parseFloat(deg) + parseFloat(min) / 60;
      const signed = (dir === 'S' || dir === 'W') ? -dd : dd;
      return isNaN(signed) ? '' : String(signed);
    },

    dmsToDD(deg, min, sec, dir) {
      const dd = parseFloat(deg) + parseFloat(min) / 60 + parseFloat(sec) / 3600;
      const signed = (dir === 'S' || dir === 'W') ? -dd : dd;
      return isNaN(signed) ? '' : String(signed);
    },

    syncDDToInputs(system) {
      if (system === 'DDM') {
        const lat = this.ddToDDM(this.sighting.lat, true);
        const lon = this.ddToDDM(this.sighting.lon, false);
        this.coordInputs = {
          latDeg: lat.deg, latMin: lat.min, latDir: lat.dir,
          lonDeg: lon.deg, lonMin: lon.min, lonDir: lon.dir,
          latSec: '', lonSec: '',
        };
      } else if (system === 'DMS') {
        const lat = this.ddToDMS(this.sighting.lat, true);
        const lon = this.ddToDMS(this.sighting.lon, false);
        this.coordInputs = {
          latDeg: lat.deg, latMin: lat.min, latSec: lat.sec, latDir: lat.dir,
          lonDeg: lon.deg, lonMin: lon.min, lonSec: lon.sec, lonDir: lon.dir,
        };
      }
      // DD binds directly to sighting.lat/lon - no coordInputs needed
    },

    syncInputsToDD() {
      if (this.coordSystem === 'DDM') {
        this.sighting.lat = this.ddmToDD(this.coordInputs.latDeg, this.coordInputs.latMin, this.coordInputs.latDir);
        this.sighting.lon = this.ddmToDD(this.coordInputs.lonDeg, this.coordInputs.lonMin, this.coordInputs.lonDir);
      } else if (this.coordSystem === 'DMS') {
        this.sighting.lat = this.dmsToDD(this.coordInputs.latDeg, this.coordInputs.latMin, this.coordInputs.latSec, this.coordInputs.latDir);
        this.sighting.lon = this.dmsToDD(this.coordInputs.lonDeg, this.coordInputs.lonMin, this.coordInputs.lonSec, this.coordInputs.lonDir);
      }
      this.sighting.coordinateDisplaySystem = this.coordSystem;
    },

    filterTimeInput(e) {
      if (!/[\d:]/.test(e.key)) e.preventDefault();
    },

    applyKnownLocation(key) {
      if (!key) return;
      const loc = enums.knownLandSightingLocations[key];
      if (!loc) return;
      this.sighting.lat = loc.lat;
      this.sighting.lon = loc.lon;
      this.sighting.locationDescription = loc.name || key.replace(/_/g, ' ').replace(/\w\S*/g, w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
      this.sighting.isNominalCoordinates = true;
      this.syncDDToInputs(this.coordSystem);
    },

    syncGroupSizeAndBnf() {
      const groups = {};
      for (const whale of this.whales) {
        const gn = whale.groupNumber;
        if (!groups[gn]) groups[gn] = { count: 0, bnf: 0 };
        groups[gn].count++;
        const bhvArray = Array.isArray(whale.bhv) ? whale.bhv : (whale.bhv ? whale.bhv.split(',') : []);
        if (bhvArray.includes('BNF')) groups[gn].bnf++;
      }
      this.sighting.totalGroupSize = String(this.whales.length);
      this.sighting.totalBnfAll = String(this.whales.filter(w => {
        const bhvArray = Array.isArray(w.bhv) ? w.bhv : (w.bhv ? w.bhv.split(',') : []);
        return bhvArray.includes('BNF');
      }).length);
      for (const whale of this.whales) {
        if (whale.groupMemberId && whale.groupMemberId.toUpperCase() === 'A') {
          const gn = whale.groupNumber;
          whale.groupSize = String(groups[gn].count);
          whale.totalBnf = String(groups[gn].bnf);
        }
      }
    },

    async save() {
      await this.$refs.form.validate();
      if (!this.valid) {
        return;
      }
      this.saveOtherBoxValues();
      this.sighting.coordinateDisplaySystem = this.coordSystem;

      console.log(this.sighting)
      console.log(this.whales)

      const sightingSnapshot = JSON.parse(JSON.stringify(this.sighting));
      const isOrcaSighting = ORCA_CODES.includes(sightingSnapshot.species);
      const expanded = [];
      for (const whale of this.whales) {
        const { features, joinTime, isiSpecies, ...whaleFields } = JSON.parse(JSON.stringify(whale));
        const bhvString = Array.isArray(whaleFields.bhv) ? whaleFields.bhv.join(',') : (whaleFields.bhv || '');
        const bhvArray = Array.isArray(whale.bhv) ? whale.bhv : (whale.bhv ? whale.bhv.split(',') : []);
        const featureList = features && features.length > 0 ? features : [{ featureName: '', photoNumber: '' }];
        for (const feat of featureList) {
          const row = { ...sightingSnapshot, ...whaleFields, bhv: bhvString, featureName: feat.featureName, photoNumber: feat.photoNumber };
          if (!isOrcaSighting) {
            delete row.ecotype;
            delete row.clan;
            delete row.pod;
            delete row.matriline;
          }
          if (bhvArray.includes('J') || bhvArray.includes('JOIN')) {
            if (joinTime) row.sightingTime = joinTime;
          }
          if (bhvArray.includes('ISI')) {
            if (isiSpecies) row.isiSpecies = isiSpecies;
          }
          expanded.push(row);
        }
      }
      this.sightings = expanded;

      console.log(this.sightings)

      if (this.sightings[0].id) {
        await this.update({ sightings: this.sightings });
      } else {
        await this.create({ sightings: this.sightings });
      }
    },

    async saveSightingTime() {
      this.formattingDate = "";
      if (
        this.hour.toString().length < 2 &&
        this.minute.toString().length < 2
      ) {
        this.formattingDate = `${this.sightingDate} 0${this.hour}:0${this.minute}:00`;
      } else if (this.hour.toString().length < 2) {
        this.formattingDate = `${this.sightingDate} 0${this.hour}:${this.minute}:00`;
      } else if (this.minute.toString().length < 2) {
        this.formattingDate = `${this.sightingDate} ${this.hour}:0${this.minute}:00`;
      } else {
        this.formattingDate = `${this.sightingDate} ${this.hour}:${this.minute}:00`;
      }
      this.sighting.sightingTime = this.formattingDate;
      this.sightingTimeDateField = false;
    },

    createMap() {
      try {
        mapboxgl.accessToken = this.accessToken;
        this.map = new mapboxgl.Map({
          container: "map",
          style: this.mapStyle,
          center: this.mapCenter,
          zoom: this.zoom,
          attributionControl: false,
        });

        this.navigation = new mapboxgl.NavigationControl();
        this.geolocation = new mapboxgl.GeolocateControl({
          positionOptions: {
            enableHighAccuracy: true
          },
        });
        this.scale = new mapboxgl.ScaleControl();

        this.map.scrollZoom.disable();
        this.map.addControl(this.navigation);
        this.map.addControl(this.geolocation);

        this.marker = new mapboxgl.Marker();

        this.map.on("click", (e) => this.moveMarker(e));
      } catch (err) {
        console.log("map error", err);
      }
    },

    moveMarker(event) {
      this.marker.setLngLat(event.lngLat).addTo(this.map);
      this.mapLocation = [event.lngLat.lng, event.lngLat.lat];
    },

    async saveLocation() {
      this.sighting.lon = this.mapLocation[0];
      this.sighting.lat = this.mapLocation[1];
      this.sighting.isNominalCoordinates = true;
      this.syncDDToInputs(this.coordSystem);
      this.mapField = false;
    },

    saveOtherBoxValues() {
      if (this.sighting.species && this.sighting.species === "Other") {
        this.sighting.species = this.specOtherValue;
      }
      if (this.sighting.type === "Other") {
        this.sighting.type = this.incOtherValue;
      }
      if (this.sighting.esct === "Other") {
        this.sighting.esct = this.locOtherValue;
      }
    },

    resetRedundantValues() {
      if (this.sighting.incidentNature !== "Entanglement") {
        this.sighting.typeOfEntanglingGear = "";
      }
      if (this.sighting.incidentNature !== "Ship Strike") {
        this.sighting.incidentVesselName = "";
        this.sighting.incidentVesselType = "";
      }
      if (!this.sighting.isDeceased) {
        this.sighting.carcassLocation = "";
      }
    },

    toggleEditConfirmDialog(dialog) {
      this.editConfirmDialog = dialog;
    },

    enableEditing() {
      this.wantEdit = true;
    },

    jumpToEdit() {
      this.requestEdit(this.sighting);
      this.close();
      this.$router.push({ name: "sightings" });
    },

    toggleEmailDialog(dialog) {
      this.emailDialog = dialog;
    },

    handleClose() {
      this.$refs.form.resetValidation();
      this.close();
      this.wantEdit = false;
    },

    addWhale() {
      this.openWhaleDialog(null);
    },

    editWhale(item) {
      this.openWhaleDialog(item);
    },

    deleteWhale(item) {
      this.selectedWhale = item;
      this.openDelete();
    },

    async removeWhaleFromList() {
      await this.removeFromSightingWhales(this.selectedWhale.whaleId);
    },
  },
};
</script>

<style>
.map {
  height: 67vh;
  width: 40rem;
  margin: 5vh 0.7rem 0 0.7rem;
}

.map-buttons {
  background-color: white;
  padding: 0.5rem 1rem 0;
}

.map-container {
  position: relative !important;
  margin-top: -2.5rem;
  background-color: white;
}


.divider {
  border-top-width: 3px;
}

.bottom-sticky {
  position: sticky;
  bottom: 0;
  z-index: 998;
}

.arrived-at-date-picker {
  background-color: white;
}

.date-picker-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.time-input-row {
  display: flex;
  align-items: center;
  width: 100%;
}
</style>
